import React, { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { useAuth } from './hooks/useAuth';
import { useData } from './hooks/useData';
import { usePWA } from './hooks/usePWA';
import { useTheme } from './hooks/useTheme';
import { Header } from './components/Header';
import { BottomNav, TabType } from './components/BottomNav';
import { TimelineView } from './components/TimelineView';
import { ActionsView } from './components/ActionsView';
import { InsightsView } from './components/InsightsView';
import { SettingsView } from './components/SettingsView';
import { QuickAddModal } from './components/QuickAddModal';
import { QuickLogModal } from './components/QuickLogModal';
import { UnifiedMealLoggerModal } from './components/UnifiedMealLoggerModal';
import { SymptomLogModal } from './components/SymptomLogModal';
import { PoopLogModal } from './components/PoopLogModal';
import { ExerciseLogModal } from './components/ExerciseLogModal';
import { AuthModal } from './components/AuthModal';

import { BarcodeScannerModal } from './components/BarcodeScannerModal';
import { FodmapScannerModal } from './components/FodmapScannerModal';
import { PhotoLogModal } from './components/PhotoLogModal';
import { DescribeLogModal } from './components/DescribeLogModal';
import { PwaInstallInstructionsModal } from './components/PwaInstallInstructionsModal';
import { Meal, Symptom, MealType, CheckinType, Exercise } from './lib/types';
import { formatIsoDate, getDefaultMealType } from './lib/dateUtils';
import { localStore, STORAGE_ERROR_EVENT, StorageErrorDetail } from './lib/storage';
import { Download, RefreshCw, AlertTriangle, X } from 'lucide-react';
import { APP_VERSION } from './version';

const TAB_ORDER: TabType[] = ['timeline', 'actions', 'insights', 'settings'];

export const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('timeline');
  const [visitedTabs, setVisitedTabs] = useState<Set<TabType>>(() => new Set(['timeline']));
  const [slideDirection, setSlideDirection] = useState<'right' | 'left'>('right');
  const mainScrollRef = useRef<HTMLElement>(null);

  const handleTabChange = (newTab: TabType) => {
    const oldIdx = TAB_ORDER.indexOf(activeTab);
    const newIdx = TAB_ORDER.indexOf(newTab);
    setSlideDirection(newIdx >= oldIdx ? 'right' : 'left');
    setActiveTab(newTab);
    setVisitedTabs((prev) => {
      if (prev.has(newTab)) return prev;
      const next = new Set(prev);
      next.add(newTab);
      return next;
    });

    // Instantly scroll up to the top of the page on tab click
    if (mainScrollRef.current) {
      mainScrollRef.current.scrollTop = 0;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  };

  useEffect(() => {
    if (mainScrollRef.current) {
      mainScrollRef.current.scrollTop = 0;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [activeTab]);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      SplashScreen.hide().catch(() => {});
    }
  }, []);

  // Dedicated Modals State
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [isPhotoLogOpen, setIsPhotoLogOpen] = useState(false);
  const [isDescribeLogOpen, setIsDescribeLogOpen] = useState(false);
  const [isSymptomLogOpen, setIsSymptomLogOpen] = useState(false);
  const [isPoopLogOpen, setIsPoopLogOpen] = useState(false);
  const [isBarcodeScannerOpen, setIsBarcodeScannerOpen] = useState(false);
  const [isFodmapScannerOpen, setIsFodmapScannerOpen] = useState(false);
  const [fodmapScannerInitialMode, setFodmapScannerInitialMode] = useState<'search' | 'barcode' | 'photo'>('search');
  const [isExerciseLogOpen, setIsExerciseLogOpen] = useState(false);
  const [isPwaInstructionsOpen, setIsPwaInstructionsOpen] = useState(false);
  const [isQuickLogOpen, setIsQuickLogOpen] = useState(false);
  const [isUnifiedMealLoggerOpen, setIsUnifiedMealLoggerOpen] = useState(false);
  const [unifiedLoggerInitialTab, setUnifiedLoggerInitialTab] = useState<
    'search' | 'scan' | 'ai' | 'library' | null
  >('search');

  // Fresh logger state for every opening, without remounting on close (which would cut the exit animation).
  const unifiedLoggerSessionRef = useRef(0);
  const wasUnifiedLoggerOpenRef = useRef(false);
  if (isUnifiedMealLoggerOpen && !wasUnifiedLoggerOpenRef.current) unifiedLoggerSessionRef.current += 1;
  wasUnifiedLoggerOpenRef.current = isUnifiedMealLoggerOpen;

  // Timeline Header Actions State
  const [isTimelineCalendarOpen, setIsTimelineCalendarOpen] = useState(false);
  const [isTimelineFilterOpen, setIsTimelineFilterOpen] = useState(false);
  const [isTimelineSearchOpen, setIsTimelineSearchOpen] = useState(false);

  // Meal & Symptom Editing States
  const [defaultMealType, setDefaultMealType] = useState<MealType | undefined>(undefined);
  const [editingMeal, setEditingMeal] = useState<Meal | null>(null);
  const [editingSymptom, setEditingSymptom] = useState<Symptom | null>(null);
  const [editingExercise, setEditingExercise] = useState<Exercise | null>(null);

  // Authentication state
  const {
    user,
    userId,
    login,
    register,
    logout,
    updateAccountName,
    deleteAccount,
    isLoading: isAuthLoading,
    authError,
    setAuthError,
  } = useAuth();

  // Initialize theme (Auto / Light / Dark)
  useTheme();

  const {
    meals,
    symptoms,
    exercises,
    insights,
    syncStatus,
    pendingSyncCount,
    isSyncing,
    isAiRunning,
    addMeal,
    updateMeal,
    deleteMeal,
    addSymptom,
    updateSymptom,
    deleteSymptom,
    addExercise,
    updateExercise,
    deleteExercise,
    runAiReanalysis,
    applySymptomRepairs,
    refreshFromCloud,
    restoreRecoverableData,
    clearAllData,
    loadSampleData,
    canUndo,
    canRedo,
    undo,
    redo,
  } = useData(userId);

  const { isInstallable, isInstalled, triggerInstall, isOnline, needRefresh, updateApp } = usePWA();
  const [isPreviewingUpdateOverlay, setIsPreviewingUpdateOverlay] = useState(false);

  const isAnyOverlayOpen = Boolean(
    !user ||
    needRefresh ||
    isPreviewingUpdateOverlay ||
    isPwaInstructionsOpen ||
    isQuickAddOpen ||
    isPhotoLogOpen ||
    isDescribeLogOpen ||
    isSymptomLogOpen ||
    isPoopLogOpen ||
    isBarcodeScannerOpen ||
    isFodmapScannerOpen ||
    isExerciseLogOpen ||
    isQuickLogOpen ||
    isUnifiedMealLoggerOpen ||
    isTimelineCalendarOpen ||
    isTimelineSearchOpen
  );

  const handleLogout = () => {
    setIsQuickAddOpen(false);
    setIsPhotoLogOpen(false);
    setIsDescribeLogOpen(false);
    setIsSymptomLogOpen(false);
    setIsPoopLogOpen(false);
    setIsBarcodeScannerOpen(false);
    setIsFodmapScannerOpen(false);
    setIsExerciseLogOpen(false);
    setIsQuickLogOpen(false);
    hasHandledInitialOpenRef.current = false;
    logout();
  };

  useEffect(() => {
    if (isAnyOverlayOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      if (mainScrollRef.current) {
        mainScrollRef.current.style.overflow = 'hidden';
      }
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      if (mainScrollRef.current) {
        mainScrollRef.current.style.overflow = 'auto';
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      if (mainScrollRef.current) {
        mainScrollRef.current.style.overflow = 'auto';
      }
    };
  }, [isAnyOverlayOpen]);

  const hasHandledInitialOpenRef = useRef(false);

  // Local saves that fail (usually because phone storage is full) are shown instead of only logged.
  const [storageError, setStorageError] = useState<StorageErrorDetail | null>(null);
  useEffect(() => {
    const onStorageError = (e: Event) => setStorageError((e as CustomEvent<StorageErrorDetail>).detail);
    window.addEventListener(STORAGE_ERROR_EVENT, onStorageError);
    return () => window.removeEventListener(STORAGE_ERROR_EVENT, onStorageError);
  }, []);

  // Close all log views if the user is not logged in
  useEffect(() => {
    if (!user) {
      setIsQuickAddOpen(false);
      setIsPhotoLogOpen(false);
      setIsDescribeLogOpen(false);
      setIsSymptomLogOpen(false);
      setIsPoopLogOpen(false);
      setIsBarcodeScannerOpen(false);
      setIsFodmapScannerOpen(false);
      setIsExerciseLogOpen(false);
      setIsQuickLogOpen(false);
      hasHandledInitialOpenRef.current = false;
    }
  }, [user]);

  // Handle URL parameters or default log view on launch ONLY if user is logged in
  useEffect(() => {
    if (!user) return;
    if (hasHandledInitialOpenRef.current) return;
    hasHandledInitialOpenRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    const type = params.get('type');
    const tab = params.get('tab');

    if (tab === 'timeline' || tab === 'actions' || tab === 'insights' || tab === 'settings') {
      setActiveTab(tab as TabType);
      setVisitedTabs((prev) => new Set(prev).add(tab as TabType));
    }

    if (action === 'log') {
      setEditingMeal(null);
      setEditingSymptom(null);
      if (type === 'poop' || type === 'stool') {
        setIsPoopLogOpen(true);
      } else if (type === 'symptom') {
        setIsSymptomLogOpen(true);
      } else if (type === 'photo') {
        setIsPhotoLogOpen(true);
      } else if (type === 'describe') {
        setIsDescribeLogOpen(true);
      } else {
        setIsQuickAddOpen(true);
      }
    } else if (action === 'morning') {
      setEditingMeal(null);
      setEditingSymptom(null);
      setIsSymptomLogOpen(true);
    }
    // Normal launch: lands cleanly on timeline without opening any modal
  }, [user]);

  const handleSaveExercise = async (exerciseData: Omit<Exercise, 'id'>, id?: string) => {
    if (id) {
      await updateExercise({ ...exerciseData, id });
    } else {
      await addExercise(exerciseData);
    }
    setEditingExercise(null);
  };

  const handleOpenLogExercise = () => {
    setEditingMeal(null);
    setEditingSymptom(null);
    setEditingExercise(null);
    setIsExerciseLogOpen(true);
  };

  const handleEditExercise = (exercise: Exercise) => {
    setEditingMeal(null);
    setEditingSymptom(null);
    setEditingExercise(exercise);
    setIsExerciseLogOpen(true);
  };

  const handleDeleteExercise = async (id: string) => {
    await deleteExercise(id);
    setEditingExercise(null);
  };

  const handleOpenLogMeal = (type?: MealType) => {
    setEditingMeal(null);
    setEditingSymptom(null);
    setDefaultMealType(type || getDefaultMealType());
    setUnifiedLoggerInitialTab('search');
    setIsUnifiedMealLoggerOpen(true);
  };

  const handleOpenLogSymptom = () => {
    setEditingMeal(null);
    setEditingSymptom(null);
    setIsSymptomLogOpen(true);
  };

  const handleEditMeal = (meal: Meal) => {
    setEditingSymptom(null);
    setEditingMeal(meal);
    setDefaultMealType(meal.meal_type);
    setUnifiedLoggerInitialTab(null);
    setIsUnifiedMealLoggerOpen(true);
  };

  const handleEditSymptom = (symptom: Symptom) => {
    setEditingMeal(null);
    setEditingSymptom(symptom);
    const isStool =
      (symptom.stool_type !== null && symptom.stool_type !== undefined) ||
      Boolean(symptom.symptom_tags && /bowel movement|stool/i.test(symptom.symptom_tags));
    if (isStool) {
      setIsPoopLogOpen(true);
    } else {
      setIsSymptomLogOpen(true);
    }
  };

  const handleBarcodeProductSelected = (product: { name: string; ingredients: string[] }) => {
    // Populate draft meal from scanned product
    const draftMeal: Meal = {
      id: '',
      date: formatIsoDate(new Date()),
      timestamp_iso: new Date().toISOString(),
      meal_type: defaultMealType || getDefaultMealType(),
      description: product.name,
      ingredients_tags: product.ingredients.join(', '),
      notes: '',
    };
    setEditingMeal(draftMeal);
    setEditingSymptom(null);
    setUnifiedLoggerInitialTab(null);
    setIsUnifiedMealLoggerOpen(true);
  };

  const handleOpenFodmapScanner = (mode: 'search' | 'barcode' | 'photo' = 'search') => {
    setFodmapScannerInitialMode(mode);
    setIsFodmapScannerOpen(true);
  };

  const handleFodmapMealLogged = (scannedMeal: { name: string; ingredients: string[] }) => {
    const draftMeal: Meal = {
      id: '',
      date: formatIsoDate(new Date()),
      timestamp_iso: new Date().toISOString(),
      meal_type: defaultMealType || getDefaultMealType(),
      description: scannedMeal.name,
      ingredients_tags: scannedMeal.ingredients.join(', '),
      notes: 'Checked with Low FODMAP Scanner',
    };
    setEditingMeal(draftMeal);
    setEditingSymptom(null);
    setUnifiedLoggerInitialTab(null);
    setIsUnifiedMealLoggerOpen(true);
  };

  const handleQuickMealSelected = (meal: Meal) => {
    // Pre-populate with recent meal ingredients and dish name
    const draftMeal: Meal = {
      id: '',
      date: formatIsoDate(new Date()),
      timestamp_iso: new Date().toISOString(),
      meal_type: meal.meal_type || getDefaultMealType(),
      description: meal.description,
      ingredients_tags: meal.ingredients_tags,
      notes: '',
    };
    setEditingMeal(draftMeal);
    setEditingSymptom(null);
    setIsQuickLogOpen(true);
  };

  const handleDeleteAccount = async (): Promise<boolean> => {
    if (user?.userId) {
      localStore.clearUserData(user.userId);
    }
    const ok = await deleteAccount();
    if (ok) {
      clearAllData();
      setActiveTab('timeline');
    }
    return ok;
  };

  return (
    <div className="bg-[#F8F9FA] dark:bg-[#0D0E11] text-slate-900 dark:text-white min-h-screen flex flex-col max-w-md mx-auto relative antialiased select-none shadow-2xl overflow-x-hidden w-full transition-colors font-[-apple-system,BlinkMacSystemFont,'SF_Pro_Text',sans-serif]">
      {/* Fullscreen App Update Overlay */}
      {(needRefresh || isPreviewingUpdateOverlay) && (
        <div className="fixed inset-0 z-[10000] bg-slate-950/95 dark:bg-black/95 backdrop-blur-md flex items-center justify-center p-6 animate-fade-in text-white">
          <div className="w-full max-w-sm p-6 sm:p-7 rounded-3xl bg-slate-900 border border-emerald-500/40 shadow-2xl text-center space-y-5">
            <div className="w-16 h-16 rounded-3xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center mx-auto shadow-inner">
              <RefreshCw className="w-8 h-8 animate-spin" />
            </div>

            <div className="space-y-2">
              <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-[11px] font-black tracking-wide uppercase">
                {isPreviewingUpdateOverlay ? `Update Preview (v${APP_VERSION})` : `New Version Ready (v${APP_VERSION})`}
              </span>
              <h3 className="text-xl font-black text-white tracking-tight">
                Update DigestLog
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed font-medium">
                A new version of DigestLog is ready with performance upgrades, bug fixes, and latest features. Please update now to reload the latest version.
              </p>
            </div>

            <div className="pt-2 space-y-2.5">
              <button
                type="button"
                onClick={() => {
                  if (needRefresh) {
                    updateApp();
                  } else {
                    window.location.reload();
                  }
                }}
                className="w-full py-3.5 px-5 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-sm tracking-wide shadow-lg active:scale-95 transition-all flex items-center justify-center space-x-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Update & Refresh Now</span>
              </button>

              {isPreviewingUpdateOverlay && (
                <button
                  type="button"
                  onClick={() => setIsPreviewingUpdateOverlay(false)}
                  className="w-full py-2 px-4 rounded-xl text-xs font-bold text-slate-400 hover:text-white transition"
                >
                  Close Admin Preview
                </button>
              )}

              <p className="text-[10px] text-slate-400">
                All your meal logs, symptoms, and accounts are safely stored.
              </p>
            </div>
          </div>
        </div>
      )}
      {storageError && (
        <div
          role="alert"
          className="fixed inset-x-0 top-0 z-[10001] max-w-md mx-auto px-4"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
        >
          <div className="p-3 rounded-2xl bg-red-600 text-white shadow-2xl flex items-start space-x-2.5">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold">Couldn't save your {storageError.what} on this device</p>
              <p className="text-[11px] text-red-100 leading-snug">
                {storageError.quotaExceeded
                  ? 'App storage on this device is full. If you are online the entry was still sent to the cloud, but it may be missing here after a restart.'
                  : `If you are online the entry was still sent to the cloud. (${storageError.message})`}
              </p>
            </div>
            <button type="button" onClick={() => setStorageError(null)} aria-label="Dismiss" className="p-1 -m-1 shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {/* Auth Modal if no user is signed in */}
      {!user && (
        <AuthModal
          onLogin={login}
          onRegister={register}
          isLoading={isAuthLoading}
          errorMessage={authError}
          onClearError={() => setAuthError(null)}
        />
      )}

      {/* Edge-to-Edge Minimalist Header */}
      <Header
        activeTab={activeTab}
        isOnline={isOnline}
        syncStatus={syncStatus}
        isSyncing={isSyncing}
        onOpenCalendar={() => setIsTimelineCalendarOpen(true)}
        onToggleFilter={() => setIsTimelineFilterOpen((prev) => !prev)}
        onOpenSearch={() => setIsTimelineSearchOpen(true)}
        onReanalyze={runAiReanalysis}
        onLogout={handleLogout}
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
      />

      {/* PWA Install Banner if standalone experience is installable or user wants instructions */}
      {(!isInstalled || isInstallable) && (
        <div className="px-4 pt-2">
          <div className="p-3 bg-white dark:bg-[#14161F] border border-slate-200/80 dark:border-zinc-800/80 rounded-2xl flex items-center justify-between shadow-xs">
            <div className="flex items-center space-x-2.5">
              <Download className="w-4 h-4 text-slate-900 dark:text-white animate-bounce" />
              <div>
                <p className="text-xs font-bold text-slate-900 dark:text-white">Install DigestLog App</p>
                <p className="text-[10px] text-slate-500 dark:text-zinc-400">Standalone fast mobile experience</p>
              </div>
            </div>
            <div className="flex items-center space-x-1.5">
              <button
                type="button"
                onClick={() => setIsPwaInstructionsOpen(true)}
                className="px-2.5 py-1 text-[11px] font-bold text-teal-600 dark:text-teal-400 hover:underline"
              >
                Guide
              </button>
              {isInstallable && (
                <button
                  type="button"
                  onClick={triggerInstall}
                  className="px-3 py-1 bg-slate-950 dark:bg-white text-white dark:text-black text-xs font-bold rounded-xl active:scale-95 transition-all shadow-xs"
                >
                  Install
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Scrollable View Area with Instant Tab Keep-Alive */}
      <main ref={mainScrollRef} className="flex-1 px-4 pt-2 overflow-y-auto overflow-x-hidden w-full">
        <div className="w-full">
          {visitedTabs.has('timeline') && (
            <div className={activeTab === 'timeline' ? 'block' : 'hidden'}>
              <TimelineView
                meals={meals}
                symptoms={symptoms}
                exercises={exercises}
                insights={insights}
                onOpenLogMeal={handleOpenLogMeal}
                onOpenQuickAdd={(type) => {
                  setDefaultMealType(type);
                  setIsQuickAddOpen(true);
                }}
                onOpenLogSymptom={handleOpenLogSymptom}
                onOpenLogPoop={() => setIsPoopLogOpen(true)}
                onOpenLogExercise={handleOpenLogExercise}
                onEditMeal={handleEditMeal}
                onEditSymptom={handleEditSymptom}
                onEditExercise={handleEditExercise}
                onDeleteMeal={deleteMeal}
                onDeleteSymptom={deleteSymptom}
                onDeleteExercise={handleDeleteExercise}
                isCalendarOpen={isTimelineCalendarOpen}
                onCloseCalendar={() => setIsTimelineCalendarOpen(false)}
                isFilterOpen={isTimelineFilterOpen}
                onToggleFilter={() => setIsTimelineFilterOpen((prev) => !prev)}
                isSearchOpen={isTimelineSearchOpen}
                onCloseSearch={() => setIsTimelineSearchOpen(false)}
              />
            </div>
          )}

          {visitedTabs.has('actions') && (
            <div className={activeTab === 'actions' ? 'block' : 'hidden'}>
              <ActionsView
                userId={userId}
                meals={meals}
                symptoms={symptoms}
                insights={insights}
                onOpenLogMeal={handleOpenLogMeal}
                onOpenLogPoop={() => setIsPoopLogOpen(true)}
                onUpdateMeal={updateMeal}
                onOpenFodmapScanner={handleOpenFodmapScanner}
              />
            </div>
          )}

          {visitedTabs.has('insights') && (
            <div className={activeTab === 'insights' ? 'block' : 'hidden'}>
              <InsightsView
                insights={insights}
                meals={meals}
                symptoms={symptoms}
                exercises={exercises}
                user={user}
                isAiRunning={isAiRunning}
                onReanalyze={runAiReanalysis}
                onEditMeal={handleEditMeal}
                onEditSymptom={handleEditSymptom}
              />
            </div>
          )}

          {visitedTabs.has('settings') && (
            <div className={activeTab === 'settings' ? 'block' : 'hidden'}>
              <SettingsView
                onClearData={clearAllData}
                onLoadSampleData={loadSampleData}
                onRefreshCloud={refreshFromCloud}
                onRestoreRecoverableData={restoreRecoverableData}
                onApplySymptomRepairs={applySymptomRepairs}
                isSyncing={isSyncing}
                syncStatus={syncStatus}
                pendingSyncCount={pendingSyncCount}
                meals={meals}
                symptoms={symptoms}
                exercises={exercises}
                user={user}
                onLogout={handleLogout}
                onUpdateAccountName={updateAccountName}
                onDeleteAccount={handleDeleteAccount}
                onOpenPwaInstructions={() => setIsPwaInstructionsOpen(true)}
                onTriggerUpdateOverlayPreview={() => setIsPreviewingUpdateOverlay(true)}
              />
            </div>
          )}
        </div>
      </main>

      {/* Liquid Glass Bottom Navigation Bar */}
      <BottomNav
        activeTab={activeTab}
        onChangeTab={handleTabChange}
        onOpenQuickLog={() => user && setIsQuickAddOpen(true)}
      />

      {/* 2-Tier Quick Add Modal */}
      <QuickAddModal
        isOpen={Boolean(user) && isQuickAddOpen}
        onClose={() => setIsQuickAddOpen(false)}
        onOpenMealLogger={(tab) => {
          setIsQuickAddOpen(false);
          setEditingMeal(null);
          setUnifiedLoggerInitialTab(tab || 'search');
          setIsUnifiedMealLoggerOpen(true);
        }}
        onSelectAiPhoto={() => {
          setIsQuickAddOpen(false);
          setEditingMeal(null);
          setUnifiedLoggerInitialTab('ai');
          setIsUnifiedMealLoggerOpen(true);
        }}
        onSelectDescribe={() => {
          setIsQuickAddOpen(false);
          setEditingMeal(null);
          setUnifiedLoggerInitialTab('ai');
          setIsUnifiedMealLoggerOpen(true);
        }}
        onSelectQuickMeal={(meal) => {
          setIsQuickAddOpen(false);
          handleQuickMealSelected(meal);
        }}
        onOpenSymptom={() => {
          setIsQuickAddOpen(false);
          setIsSymptomLogOpen(true);
        }}
        onOpenPoopLog={() => {
          setIsQuickAddOpen(false);
          setIsPoopLogOpen(true);
        }}
        onOpenExercise={() => {
          setIsQuickAddOpen(false);
          setIsExerciseLogOpen(true);
        }}
        onBarcodeProductSelected={(product) => {
          setIsQuickAddOpen(false);
          handleBarcodeProductSelected(product);
        }}
        onOpenFodmapScanner={() => {
          setIsQuickAddOpen(false);
          handleOpenFodmapScanner('search');
        }}
        recentMeals={meals}
      />

      {/* Quantitative MacroFactor-Style Unified Meal Logger Modal */}
      <UnifiedMealLoggerModal
        key={unifiedLoggerSessionRef.current}
        isOpen={Boolean(user) && isUnifiedMealLoggerOpen}
        onClose={() => {
          setIsUnifiedMealLoggerOpen(false);
          setEditingMeal(null);
        }}
        initialMeal={editingMeal}
        initialTab={unifiedLoggerInitialTab}
        defaultMealType={defaultMealType}
        recentMeals={meals}
        onDeleteMeal={async (mealId) => {
          await deleteMeal(mealId);
          setIsUnifiedMealLoggerOpen(false);
          setEditingMeal(null);
        }}
        onSaveMeal={async (mealData) => {
          if (editingMeal && editingMeal.id) {
            await updateMeal({ ...mealData, id: editingMeal.id });
          } else {
            await addMeal(mealData);
          }
          setIsUnifiedMealLoggerOpen(false);
          setEditingMeal(null);
        }}
      />

      <React.Suspense fallback={null}>
        {/* Dedicated AI Photo Log Modal */}
        {isPhotoLogOpen && Boolean(user) && (
          <PhotoLogModal
            isOpen={Boolean(user) && isPhotoLogOpen}
            onClose={() => setIsPhotoLogOpen(false)}
            onSaveMeal={async (mealData) => {
              await addMeal(mealData);
            }}
          />
        )}

        {/* Dedicated AI Describe Log Modal */}
        {isDescribeLogOpen && Boolean(user) && (
          <DescribeLogModal
            isOpen={Boolean(user) && isDescribeLogOpen}
            onClose={() => setIsDescribeLogOpen(false)}
            onSaveMeal={async (mealData) => {
              await addMeal(mealData);
            }}
          />
        )}

        {/* Standalone Barcode Scanner Modal */}
        {isBarcodeScannerOpen && Boolean(user) && (
          <BarcodeScannerModal
            isOpen={Boolean(user) && isBarcodeScannerOpen}
            onClose={() => setIsBarcodeScannerOpen(false)}
            onSelectProduct={(product: { name: string; ingredients: string[] }) => {
              setIsBarcodeScannerOpen(false);
              handleBarcodeProductSelected(product);
            }}
          />
        )}

        {/* Standalone Low FODMAP Scanner Modal */}
        {isFodmapScannerOpen && Boolean(user) && (
          <FodmapScannerModal
            isOpen={Boolean(user) && isFodmapScannerOpen}
            onClose={() => setIsFodmapScannerOpen(false)}
            onLogMeal={handleFodmapMealLogged}
            initialMode={fodmapScannerInitialMode}
          />
        )}

        {/* Standalone PWA Home Screen Instructions Modal */}
        {isPwaInstructionsOpen && (
          <PwaInstallInstructionsModal
            isOpen={isPwaInstructionsOpen}
            onClose={() => setIsPwaInstructionsOpen(false)}
          />
        )}
      </React.Suspense>

      {/* Dedicated Symptom Check-In Modal */}
      <SymptomLogModal
        isOpen={Boolean(user) && isSymptomLogOpen}
        onClose={() => {
          setIsSymptomLogOpen(false);
          setEditingSymptom(null);
        }}
        editingSymptom={editingSymptom}
        onSaveSymptom={async (symptomData) => {
          await addSymptom(symptomData);
        }}
        onUpdateSymptom={async (symptomData) => {
          await updateSymptom(symptomData);
        }}
        onDeleteSymptom={async (id) => {
          await deleteSymptom(id);
        }}
      />

      {/* Standalone Bowel Movement / Stool Modal */}
      <PoopLogModal
        isOpen={Boolean(user) && isPoopLogOpen}
        onClose={() => {
          setIsPoopLogOpen(false);
          setEditingSymptom(null);
        }}
        editingSymptom={editingSymptom}
        onSave={async (symptomData) => {
          await addSymptom(symptomData);
        }}
        onUpdate={async (symptomData) => {
          await updateSymptom(symptomData);
        }}
        onDelete={async (id) => {
          await deleteSymptom(id);
        }}
      />

      {/* Standalone Exercise Log Modal */}
      <ExerciseLogModal
        isOpen={Boolean(user) && isExerciseLogOpen}
        onClose={() => {
          setIsExerciseLogOpen(false);
          setEditingExercise(null);
        }}
        editingExercise={editingExercise}
        onDeleteExercise={handleDeleteExercise}
        onSaveExercise={handleSaveExercise}
      />

      {/* Meal Review / Edit Modal (for quick repeat & barcode confirm) */}
      <QuickLogModal
        isOpen={Boolean(user) && isQuickLogOpen}
        onClose={() => {
          setIsQuickLogOpen(false);
          setEditingMeal(null);
          setEditingSymptom(null);
        }}
        onAddMeal={addMeal}
        onUpdateMeal={updateMeal}
        onDeleteMeal={deleteMeal}
        onAddSymptom={addSymptom}
        onUpdateSymptom={updateSymptom}
        onDeleteSymptom={deleteSymptom}
        recentMeals={meals}
        initialTab="meal"
        defaultMealType={defaultMealType}
        editingMeal={editingMeal}
        editingSymptom={editingSymptom}
      />
    </div>
  );
};
